import React, { FC } from 'react';
import styled from 'styled-components';
import { fitEmojiPx } from '../../../emojis/components/emojiFit';
import { isAuthorSlot, isOpenSlot, Mark, QuizRow, SLOTS_PER_ROW } from './quizModel';

export const QUIZ_COLORS = {
  brick: '#b4532a',
  brickWash: '#fdf0ea',
  brickInk: '#7c2d12',
  yellow: '#e2b203',
  yellowWash: '#fff6c2',
  yellowInk: '#713f12',
  exact: '#16a34a',
  exactWash: '#dcfce7',
  misplaced: '#d97706',
  misplacedWash: '#fef3c7',
  wrong: '#d1d5db',
  wrongWash: '#f3f4f6',
  label: '#9ca3af',
  ink2: '#4b5563',
  line: '#e5e7eb',
} as const;

const HATCH = 'repeating-linear-gradient(45deg, #f3f4f6 0 3px, #ffffff 3px 6px)';

const Tile = styled.span<{ $size: number; $border: string; $bg: string; $dashed?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border: 1px ${(p) => (p.$dashed ? 'dashed' : 'solid')} ${(p) => p.$border};
  background: ${(p) => p.$bg};
  border-radius: 1px;
  /* sized per device: Android's emojis draw bigger and would cover the frame */
  font-size: ${(p) => fitEmojiPx(p.$size * 0.68)}px;
  line-height: 1;
`;

const Strip = styled.span<{ $gap: number }>`
  display: inline-flex;
  align-items: center;
  gap: ${(p) => p.$gap}px;
`;

function tileStyle(i: number, mark: Mark | undefined, open: boolean, hasPick: boolean) {
  if (!open) return { border: '#e5e7eb', bg: HATCH, dashed: false };
  if (mark && mark !== 'blank' && mark !== 'closed') {
    const c = QUIZ_COLORS;
    if (mark === 'exact') return { border: c.exact, bg: c.exactWash, dashed: false };
    if (mark === 'misplaced') return { border: c.misplaced, bg: c.misplacedWash, dashed: false };
    return { border: c.wrong, bg: c.wrongWash, dashed: false };
  }
  const author = isAuthorSlot(i);
  return {
    border: author ? QUIZ_COLORS.yellow : QUIZ_COLORS.brick,
    bg: author ? QUIZ_COLORS.yellowWash : QUIZ_COLORS.brickWash,
    dashed: !hasPick,
  };
}

interface GuessTilesProps {
  rows: QuizRow[];
  picks: (string | null)[];
  marks?: Mark[];
  size?: number;
  /** Hide the picks (used for "?" tiles). */
  masked?: boolean;
}

/** A guess drawn small: four tiles per author row, a gap before the yellow one. */
export const GuessTiles: FC<GuessTilesProps> = ({ rows, picks, marks, size = 14, masked }) => (
  <Strip $gap={size >= 18 ? 4 : 3} style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
    {rows.map((_, r) => (
      <Strip key={r} $gap={1}>
        {Array.from({ length: SLOTS_PER_ROW }, (_, p) => {
          const i = r * SLOTS_PER_ROW + p;
          const open = isOpenSlot(rows, i);
          const pick = picks[i];
          const s = tileStyle(i, marks?.[i], open, !!pick);
          return (
            <Tile
              key={i}
              $size={size}
              $border={s.border}
              $bg={s.bg}
              $dashed={s.dashed}
              style={p === SLOTS_PER_ROW - 1 ? { marginLeft: Math.max(2, Math.round(size / 4)) } : undefined}
              aria-label={open ? (pick && !masked ? pick : 'empty') : 'closed'}
            >
              {open && pick ? (masked ? '?' : pick) : ''}
            </Tile>
          );
        })}
      </Strip>
    ))}
  </Strip>
);
